import React, { useState, useEffect, useRef, useCallback } from 'react';
import './index.css';
import LoginView from './LoginView';
import SettingsView from './SettingsView';
import CustomersView from './CustomersView';
import KanbanBoardView from './KanbanBoardView';
import ConsultantsView from './ConsultantsView';
import TimesheetsView from './TimesheetsView';
import AnalyticsView from './AnalyticsView';
import EmployeesView from './EmployeesView';
import VacanciesView from './VacanciesView';
import MailingView from './MailingView';
import TemplatesView from './TemplatesView';
import ConsultantDatabaseView from './ConsultantDatabaseView';
import ActiveContractsView from './ActiveContractsView';
import InvoicesView from './InvoicesView';
import ConsultantPortalView from './ConsultantPortalView';
import ClientPortalView from './ClientPortalView';
import SchedulerView from './SchedulerView';

import { app, auth } from './firebase';
import { getFirestore, collection, onSnapshot, query, where, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const db = getFirestore(app);

function App() {
  // --- 1. PORTAL ROUTING CHECK ---
  const isConsultantPortal = window.location.pathname.startsWith('/consultant-portal');
  const isClientPortal = window.location.pathname.startsWith('/client-portal');
  const isPortalMode = isConsultantPortal || isClientPortal;

  // --- STATE ---
  const [user, setUser] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null); 
  const [role, setRole] = useState(null); 
  const [activeTab, setActiveTab] = useState('dashboard');
  const [authLoading, setAuthLoading] = useState(true);
  
  // UX & Layout State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [toasts, setToasts] = useState([]); // Toast Notification Queue

  // Global Data
  const [consultantDb, setConsultantDb] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [activeContracts, setActiveContracts] = useState([]); 
  const [invoices, setInvoices] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Personal Kanban Items for Dashboard
  const [myKanbanItems, setMyKanbanItems] = useState([]);

  // Navigation / Deep Linking
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);

  // --- IDLE TIMER REFS ---
  const idleTimerRef = useRef(null);
  const events = ['mousemove', 'keydown', 'click', 'scroll'];

  // --- TOAST SYSTEM ---
  const addToast = (message, type = 'success') => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, message, type }]);
      setTimeout(() => removeToast(id), 4000);
  };
  
  const removeToast = (id) => {
      setToasts(prev => prev.filter(t => t.id !== id));
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // --- EFFECTS ---

  // Apply Theme
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    if (isPortalMode) {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
        return;
    }

    const themes = ['dark', 'light', 'oled', 'dracula', 'neon'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    setTheme(nextTheme);
    
    // Persist to Firestore
    if (employeeProfile && employeeProfile.id) {
        updateDoc(doc(db, "employees", employeeProfile.id), {
            "settings.theme": nextTheme
        }).catch(err => console.error("Failed to sync theme toggle:", err));
    }
  };

  // 1. AUTH LISTENER
  useEffect(() => {
    if (isPortalMode) return; 

    const safetyTimeout = setTimeout(() => {
        if (authLoading) {
            console.warn("Auth check timed out. Defaulting to logged out state.");
            setAuthLoading(false);
        }
    }, 2000);

    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      clearTimeout(safetyTimeout); 
      
      if (currentUser) {
          try {
              const tokenResult = await currentUser.getIdTokenResult(true);
              
              if (tokenResult.claims.role === 'consultant' || tokenResult.claims.role === 'client') {
                  console.warn("Portal User attempted to login to Admin App.");
                  await signOut(auth);
                  alert("Access Denied: Please use the appropriate portal.");
                  setUser(null);
                  setAuthLoading(false);
                  return;
              }

              const empQuery = query(collection(db, "employees"), where("email", "==", currentUser.email));
              const empSnap = await getDocs(empQuery);

              if (empSnap.empty) {
                  console.warn("User not found in Employee Database.");
                  await signOut(auth);
                  alert("Access Denied: Your account is not authorized as an Employee.");
                  setUser(null);
                  setAuthLoading(false);
                  return;
              }

              // Check Claims
              if (tokenResult.claims.role !== 'internal') {
                  console.log("Incorrect claims detected. Attempting repair...");
                  try {
                      const functions = getFunctions(getApp(), 'europe-west1');
                      const repairFn = httpsCallable(functions, 'repairAdminClaims');
                      await repairFn();
                      await currentUser.getIdToken(true);
                      console.log("Account repaired successfully.");
                  } catch (err) {
                      console.error("Repair failed:", err);
                  }
              }

          } catch (e) {
              console.error("Security Check Failed:", e);
              await signOut(auth);
              setUser(null);
              setAuthLoading(false);
              return;
          }
      }

      setUser(currentUser);
      setAuthLoading(false);
    });

    return () => {
        unsubAuth();
        clearTimeout(safetyTimeout);
    };
  }, [isPortalMode]);

  // 2. PROFILE LISTENER
  useEffect(() => {
    if (!user || isPortalMode) {
        setEmployeeProfile(null);
        setRole(null);
        return;
    }

    const q = query(collection(db, "employees"), where("email", "==", user.email));
    const unsubProfile = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
            const docData = snapshot.docs[0].data();
            setEmployeeProfile({ id: snapshot.docs[0].id, ...docData });
            setRole(docData.accessRole || 'Read-Only Visitor');

            // Sync theme from profile
            if (docData.settings && docData.settings.theme) {
                setTheme(docData.settings.theme);
            }
        } else {
            setRole('Read-Only Visitor');
            setEmployeeProfile(null);
        }
    }, (error) => {
        console.error("Profile fetch error:", error);
    });

    return () => unsubProfile();
  }, [user, isPortalMode]);

  // 3. FETCH USER'S KANBAN ITEMS (For Dashboard)
  useEffect(() => {
      if (!user || !employeeProfile || isPortalMode) return;

      const fetchMyItems = async () => {
          try {
              const functions = getFunctions(getApp(), 'europe-west1');
              const getItemsFn = httpsCallable(functions, 'getKanbanItems');
              
              const result = await getItemsFn();
              const allItems = result.data.items || [];

              // Filter for current user & active items
              const myItems = allItems.filter(i => 
                  i.assigneeId === employeeProfile.id && 
                  !i.isArchived && 
                  i.status !== 'Done' && 
                  i.status !== 'Placed'
              );

              // Sort by Due Date (Ascending)
              myItems.sort((a, b) => new Date(a.dueDate || '9999-12-31') - new Date(b.dueDate || '9999-12-31'));

              setMyKanbanItems(myItems);
          } catch (err) {
              console.error("Error fetching dashboard tasks:", err);
          }
      };

      fetchMyItems();
      const interval = setInterval(fetchMyItems, 60000); // Refresh every minute
      return () => clearInterval(interval);
  }, [user, employeeProfile, isPortalMode]);

  // --- IDLE TIMER LOGIC ---
  const handleLogout = useCallback(() => {
    if (user) {
        console.log("Session timed out due to inactivity.");
        signOut(auth).catch(err => console.error("Logout Error:", err));
    }
  }, [user]);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    
    let timeoutDuration = 14400000; 
    
    if (employeeProfile?.settings?.loginTimeout) {
        timeoutDuration = parseFloat(employeeProfile.settings.loginTimeout) * 60 * 60 * 1000;
    }

    if (user) {
        idleTimerRef.current = setTimeout(handleLogout, timeoutDuration);
    }
  }, [user, employeeProfile, handleLogout]);

  useEffect(() => {
    if (!user || isPortalMode) return;

    resetIdleTimer();
    events.forEach(event => window.addEventListener(event, resetIdleTimer));

    return () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        events.forEach(event => window.removeEventListener(event, resetIdleTimer));
    };
  }, [user, employeeProfile, resetIdleTimer, isPortalMode]);


  // Data Listeners
  useEffect(() => {
    if (!user || isPortalMode) return;
    setDataLoading(true);

    const unsubCustomers = onSnapshot(collection(db, "customers"), (snap) => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubTimesheets = onSnapshot(collection(db, "timesheets"), (snap) => setTimesheets(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubEmployees = onSnapshot(collection(db, "employees"), (snap) => setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubVacancies = onSnapshot(collection(db, "vacancies"), (snap) => setVacancies(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubConsultantDb = onSnapshot(collection(db, "consultant_db"), (snap) => setConsultantDb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubActiveContracts = onSnapshot(collection(db, "active_contracts"), (snap) => setActiveContracts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubInvoices = onSnapshot(collection(db, "invoices"), (snap) => {
        setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setDataLoading(false);
    }, (error) => {
        console.error("Error loading invoices:", error);
        setDataLoading(false);
    });

    return () => {
      unsubCustomers(); unsubTimesheets(); unsubEmployees(); unsubVacancies(); unsubConsultantDb(); unsubActiveContracts(); unsubInvoices();
    };
  }, [user, isPortalMode]);

  const logAction = async (action, details) => {
    if (!user) return;
    try {
      await addDoc(collection(db, "audit_logs"), {
        timestamp: new Date(),
        userEmail: user.email,
        action: action,
        details: details
      });
    } catch (err) { console.error("Audit Log Failed", err); }
  };

  const navigateToConsultant = (id) => {
      setSelectedConsultantId(id);
      setActiveTab('consultant-db');
  };

  // --- RENDER ---

  if (isConsultantPortal) {
      return <ConsultantPortalView theme={theme} toggleTheme={toggleTheme} />;
  }

  if (isClientPortal) {
      return <ClientPortalView theme={theme} toggleTheme={toggleTheme} />;
  }

  if (authLoading) return <div className="vh-100 bg-transparent-theme d-flex align-items-center justify-content-center text-main">Loading Nettic...</div>;
  if (!user) return <LoginView />;

  const isReadOnly = role !== 'Super Admin';
  const isSuperAdmin = role === 'Super Admin';
  const newsItems = customers.filter(c => c.rssUrl && c.rssUrl.trim() !== "").map(c => ({ company: c.companyName, url: c.rssUrl }));

  return (
    <div className="d-flex flex-column flex-md-row" style={{ height: '100vh', overflow: 'hidden' }}>
      
      {/* MOBILE HEADER */}
      <div className="d-md-none bg-sidebar border-bottom border-theme p-3 d-flex justify-content-between align-items-center" style={{ zIndex: 1060 }}>
          <span className="fs-4 fw-bold text-main">Nettic<span className="text-accent">.</span></span>
          <button className="btn btn-link text-main p-0" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              <i className={`bi ${isMobileMenuOpen ? 'bi-x-lg' : 'bi-list'} fs-2`}></i>
          </button>
      </div>

      {/* MOBILE BACKDROP */}
      {isMobileMenuOpen && (
          <div 
            className="d-md-none position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50" 
            style={{ zIndex: 1040 }}
            onClick={closeMobileMenu}
          ></div>
      )}

      {/* TOAST CONTAINER */}
      <div className="toast-container-custom">
          {toasts.map(t => (
              <div key={t.id} className={`custom-toast ${t.type}`}>
                  <i className={`bi ${t.type === 'success' ? 'bi-check-circle-fill text-success' : (t.type === 'error' ? 'bi-exclamation-triangle-fill text-danger' : 'bi-info-circle-fill text-info')}`}></i>
                  <span>{t.message}</span>
                  <button className="btn-close ms-auto" onClick={() => removeToast(t.id)}></button>
              </div>
          ))}
      </div>

      {/* SIDEBAR */}
      <div className={`sidebar-container bg-sidebar border-end border-theme overflow-auto ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`} style={{ transition: 'all 0.3s ease' }}>
          <div className="d-flex flex-column h-100 p-3">
              <div className="d-none d-md-flex align-items-center mb-4 px-2">
                {!isSidebarCollapsed && <span className="fs-4 fw-bold text-main logo-text me-auto">Nettic<span className="text-accent">.</span></span>}
                <button className={`btn btn-sm btn-link text-muted-theme p-0 ${isSidebarCollapsed ? 'mx-auto' : ''}`} onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                    <i className={`bi ${isSidebarCollapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar-inset-reverse'} fs-5`}></i>
                </button>
              </div>
              
              {!isSidebarCollapsed && (
                  <div className="mb-4 small user-info px-2">
                      <div className="text-muted-theme">Logged in as:</div>
                      <div className="text-main fw-bold text-truncate">{user.email}</div>
                      <span className={`badge mt-1 ${isReadOnly ? 'bg-secondary' : 'bg-primary'}`}>{role}</span>
                  </div>
              )}
              
              <ul className="nav nav-pills flex-column mb-auto">
                {/* OVERVIEW */}
                <NavHeader label="Overview" collapsed={isSidebarCollapsed} />
                <NavItem icon="bi-speedometer2" label="Dashboard" active={activeTab === 'dashboard'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('dashboard'); closeMobileMenu(); }} />
                <NavItem icon="bi-kanban" label="Kanban Board" active={activeTab === 'kanban'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('kanban'); closeMobileMenu(); }} />
                <NavItem icon="bi-graph-up" label="Analytics" active={activeTab === 'analytics'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('analytics'); closeMobileMenu(); }} />
                <NavItem icon="bi-calendar-week" label="Scheduler" active={activeTab === 'scheduler'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('scheduler'); closeMobileMenu(); }} />

                {/* RECRUITMENT */}
                <NavHeader label="Recruitment" collapsed={isSidebarCollapsed} />
                <NavItem icon="bi-briefcase" label="Vacancies" active={activeTab === 'vacancies'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('vacancies'); closeMobileMenu(); }} />
                <NavItem icon="bi-person-lines-fill" label="Consultant DB" active={activeTab === 'consultant-db'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('consultant-db'); closeMobileMenu(); }} />
                <NavItem icon="bi-envelope-paper" label="Mailing" active={activeTab === 'mailing'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('mailing'); closeMobileMenu(); }} />
                <NavItem icon="bi-file-earmark-text" label="Templates" active={activeTab === 'templates'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('templates'); closeMobileMenu(); }} />

                {/* CONSULTANTS */}
                <NavHeader label="Consultants" collapsed={isSidebarCollapsed} />
                <NavItem icon="bi-people" label="Active Consultants" active={activeTab === 'consultants'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('consultants'); closeMobileMenu(); }} />
                <NavItem icon="bi-file-earmark-check" label="Active Contracts" active={activeTab === 'active-contracts'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('active-contracts'); closeMobileMenu(); }} />
                <NavItem icon="bi-file-earmark-spreadsheet" label="Timesheets" active={activeTab === 'timesheets'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('timesheets'); closeMobileMenu(); }} />

                {/* ORGANIZATION */}
                <NavHeader label="Organization" collapsed={isSidebarCollapsed} />
                <NavItem icon="bi-person-badge" label="Employees" active={activeTab === 'employees'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('employees'); closeMobileMenu(); }} />
                <NavItem icon="bi-building" label="Customers" active={activeTab === 'customers'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('customers'); closeMobileMenu(); }} />
                <NavItem icon="bi-receipt" label="Invoices" active={activeTab === 'invoices'} collapsed={isSidebarCollapsed} onClick={() => { setActiveTab('invoices'); closeMobileMenu(); }} />
              </ul>
              
              <div className="mt-auto border-top border-theme pt-2">
                  <ul className="nav nav-pills flex-column">
                      <NavItem 
                          icon="bi-gear" 
                          label="My Settings" 
                          active={activeTab === 'settings'} 
                          collapsed={isSidebarCollapsed} 
                          onClick={() => { setActiveTab('settings'); closeMobileMenu(); }} 
                      />
                      <li className="nav-item">
                          <button 
                              className="nav-link d-flex align-items-center gap-3 px-3 py-2 w-100 text-start text-danger bg-transparent border-0" 
                              onClick={() => { logAction("Logout", "User logged out"); signOut(auth); closeMobileMenu(); }}
                              title={isSidebarCollapsed ? "Logout" : ""}
                          >
                              <i className="bi bi-box-arrow-right"></i>
                              {!isSidebarCollapsed && <span>Logout</span>}
                          </button>
                      </li>
                  </ul>
              </div>
          </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-grow-1 overflow-auto p-3 p-md-4 bg-transparent-theme d-flex flex-column">
        {/* BREADCRUMB HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-3 mb-md-4 pb-3 border-bottom border-theme">
            <div>
                <nav aria-label="breadcrumb" className="d-none d-md-block">
                    <ol className="breadcrumb mb-1 small">
                        <li className="breadcrumb-item text-muted-theme">Nettic</li>
                        <li className="breadcrumb-item active text-main" aria-current="page">
                            {activeTab === 'consultant-db' ? 'Consultant Database' : activeTab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </li>
                    </ol>
                </nav>
                <h2 className="h5 h4-md fw-bold text-main mb-0">{
                    activeTab === 'dashboard' ? `Welcome back, ${employeeProfile?.name?.split(' ')[0] || 'User'}` : 
                    (activeTab === 'consultant-db' ? 'Consultant Database' :
                    activeTab === 'active-contracts' ? 'Active Contracts' :
                    activeTab === 'templates' ? 'Templates Library' :
                    activeTab === 'invoices' ? 'Invoicing' :
                    (activeTab === 'settings' ? 'Settings' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')))
                }</h2>
            </div>
            <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={toggleTheme}>
                    {theme === 'dark' ? <i className="bi bi-moon-stars-fill"></i> : <i className="bi bi-sun-fill"></i>}
                </button>
            </div>
        </div>
        
        {/* VIEWS */}
        <div className="flex-grow-1">
            {activeTab === 'dashboard' && <DashboardView activeContracts={activeContracts} timesheets={timesheets} invoices={invoices} newsItems={newsItems} loading={dataLoading} setActiveTab={setActiveTab} kanbanItems={myKanbanItems} user={user} employeeProfile={employeeProfile} employees={employees} vacancies={vacancies} />}
            
            {activeTab === 'kanban' && (
                <KanbanBoardView 
                    employees={employees} 
                    user={user} 
                    logAction={logAction} 
                    navigateToConsultant={navigateToConsultant} 
                    isSuperAdmin={isSuperAdmin}
                    isReadOnly={isReadOnly}
                    customers={customers} 
                    consultantDb={consultantDb}
                    addToast={addToast}
                />
            )}
            
            {activeTab === 'consultant-db' && (
                <ConsultantDatabaseView 
                    consultantDb={consultantDb} 
                    loading={dataLoading} 
                    logAction={logAction} 
                    isReadOnly={isReadOnly} 
                    initialSelectedId={selectedConsultantId}
                    currentUserEmail={user.email}
                    employees={employees}
                    addToast={addToast}
                />
            )}
            
            {activeTab === 'active-contracts' && <ActiveContractsView contracts={activeContracts} consultants={consultantDb} consultantDb={consultantDb} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            
            {activeTab === 'invoices' && <InvoicesView activeContracts={activeContracts} invoices={invoices} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            
            {activeTab === 'consultants' && <ConsultantsView consultantDb={consultantDb} activeContracts={activeContracts} customers={customers} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            
            {activeTab === 'customers' && <CustomersView customers={customers} vacancies={vacancies} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            {activeTab === 'employees' && <EmployeesView employees={employees} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            
            {/* UPDATED: Merged Vacancies View */}
            {activeTab === 'vacancies' && (
                <VacanciesView 
                    vacancies={vacancies} 
                    customers={customers} 
                    consultants={consultantDb} 
                    employees={employees} 
                    loading={dataLoading} 
                    logAction={logAction} 
                    isSuperAdmin={isSuperAdmin}
                    consultantDb={consultantDb} // Required for placement logic
                    currentUserEmail={user.email} // Required for My Vacancies filtering
                    addToast={addToast}
                />
            )}
            
            {activeTab === 'templates' && <TemplatesView isSuperAdmin={isSuperAdmin} logAction={logAction} addToast={addToast} />}
            
            {activeTab === 'mailing' && <MailingView vacancies={vacancies} loading={dataLoading} logAction={logAction} isReadOnly={isReadOnly} addToast={addToast} />}
            {activeTab === 'scheduler' && <SchedulerView user={user} employees={employees} consultantDb={consultantDb} logAction={logAction} addToast={addToast} />}
            
            {activeTab === 'timesheets' && <TimesheetsView activeContracts={activeContracts} consultantDb={consultantDb} isReadOnly={isReadOnly} addToast={addToast} />}
            
            {activeTab === 'analytics' && <AnalyticsView invoices={invoices} activeContracts={activeContracts} employees={employees} user={user} />}
            
            {activeTab === 'settings' && <SettingsView logAction={logAction} employeeProfile={employeeProfile} addToast={addToast} />}
        </div>
      </div>
    </div>
  );
}

// DASHBOARD WIDGETS AND HELPERS
const DashboardView = ({ activeContracts, timesheets, invoices, newsItems, loading, setActiveTab, kanbanItems, user, employeeProfile, employees, vacancies }) => {
  if (loading) {
      return (
          <div className="row g-4">
              {[1,2,3,4].map(i => <div key={i} className="col-md-3"><div className="skeleton w-100" style={{height: '100px'}}></div></div>)}
              <div className="col-12"><div className="skeleton w-100" style={{height: '300px'}}></div></div>
          </div>
      );
  }

  const today = new Date();
  const currentMonth = today.toLocaleString('default', { month: 'long' });
  
  const expiringContracts = activeContracts.filter(c => {
      if (!c.endDate || c.status === 'Terminated') return false;
      const end = new Date(c.endDate);
      const diffTime = end - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
      return diffDays >= 0 && diffDays <= 45; 
  });
  
  const overdueInvoices = invoices.filter(inv => {
      if (inv.clientStatus === 'Paid' || inv.archived) return false;
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const mIdx = months.indexOf(inv.month);
      const invEndMonth = new Date(inv.year, mIdx + 1, 0);
      const terms = parseInt(inv.clientTerms) || 30;
      invEndMonth.setDate(invEndMonth.getDate() + terms); 
      return invEndMonth < today;
  });

  // Calculate Revenue Trend Data (Last 6 Months)
  const last6Months = Array.from({length: 6}, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
      return { 
          name: d.toLocaleString('default', { month: 'short' }), 
          year: d.getFullYear().toString(),
          monthFull: d.toLocaleString('default', { month: 'long' })
      };
  });

  const chartData = last6Months.map(m => {
      const periodInvoices = invoices.filter(inv => inv.month === m.monthFull && inv.year === m.year && !inv.archived);
      const revenue = periodInvoices.reduce((sum, inv) => sum + (parseFloat(inv.calculatedAmount) || 0), 0);
      return { ...m, revenue };
  });

  const maxRev = Math.max(...chartData.map(d => d.revenue), 1000);

  return (
    <div className="row g-4">
      {/* KPI ROW */}
      <div className="col-12">
        <h5 className="mb-3" style={{color: 'var(--text-main)'}}>Overview ({currentMonth})</h5>
        <div className="row g-2 g-md-3">
          <KpiCard title="Active Contracts" value={activeContracts.filter(c => c.status === 'Active').length} icon="bi-file-text" color="text-primary" />
          <KpiCard title="Open Vacancies" value={vacancies.filter(v => v.status === 'Open').length} icon="bi-briefcase" color="text-success" />
          <KpiCard title="Overdue Invoices" value={overdueInvoices.length} icon="bi-receipt" color="text-danger" />
          <KpiCard title="Pending Timesheets" value={timesheets.filter(t => t.status === 'Submitted').length} icon="bi-clock-history" color="text-info" />
        </div>
      </div>

      {/* ALERT ROW */}
      <div className="col-12">
          {overdueInvoices.length > 0 && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center border-danger shadow-sm">
                <div><i className="bi bi-exclamation-circle-fill me-2"></i><strong>Action Required:</strong> {overdueInvoices.length} overdue invoices.</div>
                <button className="btn btn-sm btn-light text-danger fw-bold" onClick={() => setActiveTab('invoices')}>View Invoices</button>
            </div>
        )}
      </div>

      {/* ROW 2: TASKS & APPOINTMENTS (Left) | REVENUE (Right) */}
      <div className="col-lg-8">
        <div className="row g-4">
            {/* TASKS */}
            <div className="col-md-6">
                <div className="card h-100 bg-card border-theme shadow-sm" style={{minHeight: '350px'}}>
                    <div className="card-header bg-transparent border-theme d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 text-main h6"><i className="bi bi-check2-square me-2 text-accent"></i> My To-Dos</h5>
                        <button className="btn btn-sm btn-link p-0 text-muted-theme" onClick={() => setActiveTab('kanban')}>View All</button>
                    </div>
                    <div className="card-body p-0 overflow-auto">
                        <TopTasksWidget items={kanbanItems} navigate={() => setActiveTab('kanban')} employees={employees} />
                    </div>
                </div>
            </div>

            {/* APPOINTMENTS */}
            <div className="col-md-6">
                 <UpcomingAppointmentsWidget employeeId={employeeProfile?.id} navigate={() => setActiveTab('scheduler')} />
            </div>

            {/* REVENUE CHART (Wide) */}
            <div className="col-12">
                <div className="card bg-card border-theme p-4 shadow-sm">
                    <h5 className="text-main mb-4 h6">Revenue Trend (Last 6 Months)</h5>
                    <div className="d-flex align-items-end justify-content-between" style={{height: '200px'}}>
                        {chartData.map((d, i) => (
                            <div key={i} className="d-flex flex-column align-items-center w-100 group" style={{height: '100%', justifyContent: 'flex-end'}}>
                                <div className="text-success small fw-bold mb-2 opacity-0 group-hover-opacity" style={{transition: 'opacity 0.2s'}}>
                                    €{(d.revenue/1000).toFixed(1)}k
                                </div>
                                <div 
                                    className="bg-primary opacity-75 rounded-top w-50" 
                                    style={{
                                        height: `${(d.revenue / maxRev) * 100}%`, 
                                        transition: 'height 1s ease',
                                        minHeight: '4px'
                                    }}
                                ></div>
                                <div className="text-muted-theme small mt-2">{d.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* RIGHT COL: EXPIRING & ACTIONS */}
      <div className="col-lg-4">
        
        {/* EXPIRING CONTRACTS */}
        <div className="card bg-card border-theme shadow-sm mb-4">
            <div className="card-header bg-transparent border-theme">
                <h5 className="mb-0 text-main h6"><i className="bi bi-hourglass-split text-warning me-2"></i> Expiring Soon</h5>
            </div>
            <div className="card-body p-0">
                {expiringContracts.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 text-main small">
                            <tbody>
                                {expiringContracts.slice(0, 5).map(c => (
                                    <tr key={c.id}>
                                        <td className="bg-card border-theme px-3">
                                            <div className="fw-bold">{c.consultantName}</div>
                                            <div className="text-muted-theme">{c.client}</div>
                                        </td>
                                        <td className="bg-card border-theme text-end px-3">
                                            <div className="text-warning fw-bold">{new Date(c.endDate).toLocaleDateString()}</div>
                                            <button className="btn btn-sm btn-link p-0" onClick={() => setActiveTab('active-contracts')}>Renew</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : <p className="text-muted-theme p-3 mb-0 text-center small">No contracts expiring in 45 days.</p>}
            </div>
        </div>

        {/* QUICK ACTIONS */}
        <div className="card bg-card border-theme p-3 mb-4 shadow-sm">
          <h5 className="mb-3 text-main h6">Quick Actions</h5>
          <div className="d-grid gap-2">
              <button className="btn btn-outline-primary text-start" onClick={() => setActiveTab('consultant-db')}><i className="bi bi-person-plus-fill me-2"></i> Add Candidate</button>
              <button className="btn btn-outline-success text-start" onClick={() => setActiveTab('timesheets')}><i className="bi bi-upload me-2"></i> Upload Timesheet</button>
              <button className="btn btn-outline-warning text-start" onClick={() => setActiveTab('vacancies')}><i className="bi bi-briefcase-fill me-2"></i> Post Vacancy</button>
              <button className="btn btn-outline-info text-start" onClick={() => setActiveTab('scheduler')}><i className="bi bi-calendar-plus me-2"></i> Schedule Meeting</button>
          </div>
        </div>

        {/* NEWS */}
        <div className="card bg-card border-theme shadow-sm">
          <div className="card-header bg-transparent border-theme">
            <h5 className="mb-0 text-main h6"><i className="bi bi-rss text-accent me-2"></i> Client News</h5>
          </div>
          <div className="card-body p-0">
            {newsItems.length > 0 ? (
              <ul className="list-group list-group-flush">
                {newsItems.slice(0, 3).map((item, index) => (
                  <li key={index} className="list-group-item bg-transparent border-theme d-flex justify-content-between align-items-center px-3">
                    <div className="text-truncate" style={{maxWidth: '80%'}}>
                        <span className="badge bg-primary me-2 scale-75 origin-left">{item.company}</span>
                    </div>
                    <a href={item.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary py-0"><i className="bi bi-box-arrow-up-right small"></i></a>
                  </li>
                ))}
              </ul>
            ) : <p className="text-muted-theme p-3 text-center small">No news feeds configured.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- WIDGETS & COMPONENTS ---

const UpcomingAppointmentsWidget = ({ employeeId, navigate }) => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!employeeId) return;
        
        // Simple query for now - in production, compound query with date filter
        const q = query(
            collection(db, "meetings"), 
            where("attendees", "array-contains", employeeId)
        );

        const unsub = onSnapshot(q, (snap) => {
            const today = new Date();
            today.setHours(0,0,0,0);
            
            const list = snap.docs.map(d => ({id: d.id, ...d.data()}))
                .filter(m => new Date(m.date) >= today && m.status !== 'Cancelled')
                .sort((a,b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time))
                .slice(0, 5); // Take top 5 upcoming
            
            setAppointments(list);
            setLoading(false);
        });
        return () => unsub();
    }, [employeeId]);

    return (
        <div className="card h-100 bg-card border-theme shadow-sm" style={{minHeight: '350px'}}>
            <div className="card-header bg-transparent border-theme d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-main h6"><i className="bi bi-calendar-event me-2 text-info"></i> Upcoming Meetings</h5>
                <button className="btn btn-sm btn-link p-0 text-muted-theme" onClick={navigate}>View Calendar</button>
            </div>
            <div className="card-body p-0 overflow-auto">
                {loading ? <div className="p-3 text-muted-theme small">Loading...</div> : (
                    appointments.length > 0 ? (
                        <ul className="list-group list-group-flush">
                            {appointments.map(appt => (
                                <li key={appt.id} className="list-group-item bg-transparent border-theme py-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 text-info rounded p-2 text-center me-3" style={{minWidth: '50px'}}>
                                            <div className="small fw-bold">{new Date(appt.date).toLocaleString('default', {month:'short'}).toUpperCase()}</div>
                                            <div className="h5 mb-0 fw-bold">{new Date(appt.date).getDate()}</div>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="text-main fw-bold text-truncate">{appt.title}</div>
                                            <div className="text-muted-theme small">
                                                <i className="bi bi-clock me-1"></i> {appt.time} - {appt.endTime}
                                            </div>
                                            {appt.consultantName && (
                                                <div className="text-accent small text-truncate">
                                                    <i className="bi bi-person me-1"></i> {appt.consultantName}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-5">
                            <i className="bi bi-calendar-check fs-1 text-muted-theme mb-2"></i>
                            <p className="text-muted-theme small">No upcoming meetings.</p>
                            <button className="btn btn-sm btn-outline-primary" onClick={navigate}>Schedule One</button>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

const TopTasksWidget = ({ items, navigate, employees }) => {
    const topItems = items.slice(0, 5);
    const getColor = (id) => {
        const emp = employees.find(e => e.id === id);
        return emp?.profileColor || '#6c757d';
    };

    return (
        <ul className="list-group list-group-flush">
            {topItems.length > 0 ? topItems.map(item => {
                let badgeClass = 'bg-secondary';
                if(item.status === 'To Do' || item.status === 'Due for Outreach') badgeClass = 'bg-warning text-dark';
                if(item.status === 'Screening' || item.status === 'Scheduled') badgeClass = 'bg-info text-dark';
                if(item.status === 'Offer' || item.status === 'Placed') badgeClass = 'bg-success';
                
                const color = getColor(item.assigneeId);

                return (
                    <li key={item.id} className="list-group-item bg-transparent border-theme d-flex justify-content-between align-items-center py-3" style={{borderLeft: `4px solid ${color}`}}>
                        <div className="flex-grow-1 ms-2">
                            <div className="fw-bold text-main">{item.title}</div>
                            <small className="text-muted-theme d-block">{item.subtitle}</small>
                            <small className={new Date(item.dueDate) < new Date() ? "text-danger fw-bold" : "text-muted-theme"}>
                                <i className="bi bi-calendar-event me-1"></i> 
                                {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'No Date'}
                            </small>
                        </div>
                        <span className={`badge ${badgeClass}`}>{item.status}</span>
                    </li>
                );
            }) : (
                <div className="text-center py-5">
                    <i className="bi bi-check2-all fs-1 text-muted-theme mb-2"></i>
                    <p className="text-muted-theme">All caught up! No active tasks.</p>
                    <button className="btn btn-sm btn-outline-primary" onClick={navigate}>Go to Board</button>
                </div>
            )}
        </ul>
    );
};



const KpiCard = ({ title, value, icon, color }) => (
  <div className="col-6 col-xl-3">
    <div className="card h-100 bg-card border-theme shadow-sm">
      <div className="card-body d-flex align-items-center p-2 p-md-3">
        <div className={`rounded-circle bg-body border border-theme p-2 p-md-3 me-2 me-md-3 d-flex align-items-center justify-content-center ${color}`} style={{ width: '40px', height: '40px', minWidth: '40px', fontSize: '1rem' }}>
            <i className={`bi ${icon}`}></i>
        </div>
        <div className="overflow-hidden">
            <h6 className="text-muted-theme mb-0 small text-truncate">{title}</h6>
            <h4 className="fw-bold mb-0 text-main">{value}</h4>
        </div>
      </div>
    </div>
  </div>
);

// SIDEBAR HELPERS
const NavHeader = ({ label, collapsed }) => collapsed ? <hr className="border-theme mx-2 my-2"/> : <li className="nav-item mt-3 mb-2 px-2 text-uppercase small fw-bold text-muted-theme" style={{fontSize: '0.75rem', letterSpacing: '0.5px'}}>{label}</li>;
const NavItem = ({ icon, label, active, onClick, collapsed }) => (
    <li className="nav-item mb-1">
        <a href="#" onClick={(e) => { e.preventDefault(); onClick(); }} 
           className={`nav-link d-flex align-items-center gap-3 px-3 py-2 ${active ? 'active' : ''}`} 
           title={collapsed ? label : ''}
        >
            <i className={`bi ${icon}`}></i>
            {!collapsed && <span>{label}</span>}
        </a>
    </li>
);
const Header = ({ title, toggleSidebar }) => (
    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-4 border-bottom border-theme">
        <div className="d-flex align-items-center">
            <button className="btn btn-link text-main p-0 me-3 border-0 d-md-none" onClick={toggleSidebar}>
                <i className="bi bi-list fs-3"></i>
            </button>
            <h1 className="h2 mb-0" style={{color: 'var(--text-main)'}}>{title}</h1>
        </div>
    </div>
);

export default App;